'use client';

import { useEffect, useMemo, useState } from 'react';
import { fetchRelationships, type RelationshipRow } from '@/lib/api';
import { GAMES } from '@/lib/games';
import { S_CARD, S_MUTED } from './settingsUi';

/**
 * Settings ▸ Relationship — everyone this account has played online co-op with,
 * closest first (backend lib/relationship.ts).
 *
 * ── Why this is not a list of numbers (owner, 2026-10-12) ───────────────────
 * It used to show the raw score and nothing else, which told you a figure but
 * never what it MEANT or how to move it. A relationship reads the way every
 * social product shows one: a named STANDING you can recognise (the six tiers
 * the server already labels), visible PROGRESS to the next one, and the handful
 * of facts that earned it — missions together, how many you survived, when you
 * last fought side by side. The score is still there, but as the small print
 * under the standing rather than the headline.
 *
 * The closest squadmate gets a hero card, because "who am I tightest with" is
 * the one question this screen exists to answer.
 */

/**
 * The six standings `bondTier()` hands back, with where each begins and what it
 * takes to reach the next. Kept in step with backend/src/lib/relationship.ts —
 * if the thresholds move there, move them here.
 *
 * Class strings are written out in full on purpose: Tailwind only keeps classes
 * it can SEE, so a built-up `text-${c}-600` would be dropped from the bundle.
 */
const TIERS: { name: string; min: number; next: number | null; chip: string; bar: string; dot: string; icon: string }[] = [
  { name: 'Rocky', min: -Infinity, next: 0, icon: '!', dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-500/30', bar: 'bg-red-400' },
  { name: 'Met', min: 0, next: 1, icon: '·', dot: 'bg-ink-400',
    chip: 'bg-ink-100 text-ink-600 ring-ink-200 dark:bg-white/5 dark:text-ink-300 dark:ring-white/10', bar: 'bg-ink-400' },
  { name: 'New squad', min: 1, next: 20, icon: '◔', dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-500/30', bar: 'bg-sky-400' },
  { name: 'Allies', min: 20, next: 50, icon: '◑', dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-500/30', bar: 'bg-emerald-400' },
  { name: 'Trusted', min: 50, next: 100, icon: '◕', dot: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-500/30', bar: 'bg-violet-400' },
  { name: 'Battle-tested', min: 100, next: 200, icon: '★', dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-500/30', bar: 'bg-amber-400' },
  { name: 'Inseparable', min: 200, next: null, icon: '◆', dot: 'bg-rose-500',
    chip: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-500/30', bar: 'bg-rose-400' },
];

function tierOf(row: RelationshipRow) {
  // Trust the server's label when we know it; fall back to the score so a new
  // tier added on the backend still renders instead of blanking the row.
  return TIERS.find((t) => t.name === row.tier)
    ?? [...TIERS].reverse().find((t) => row.score >= t.min)
    ?? TIERS[1];
}

/** How far along this standing they are, and what is left to the next one. */
function progressOf(row: RelationshipRow) {
  const t = tierOf(row);
  if (t.next == null) return { pct: 1, toNext: 0, nextName: null as string | null };
  const floor = t.min === -Infinity ? Math.min(row.score, 0) : t.min;
  const span = t.next - floor;
  const pct = span > 0 ? Math.max(0, Math.min(1, (row.score - floor) / span)) : 0;
  const nextName = TIERS[TIERS.indexOf(t) + 1]?.name ?? null;
  return { pct, toNext: Math.max(0, t.next - row.score), nextName };
}

export default function RelationshipSettings() {
  const [rows, setRows] = useState<RelationshipRow[] | null>(null);
  const [rules, setRules] = useState<{ clear: number; fail: number; dailyGainCap: number } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetchRelationships()
      .then((r) => { if (live) { setRows(r.rows ?? []); setRules(r.rules ?? null); } })
      .catch(() => { if (live) { setRows([]); setFailed(true); } });
    return () => { live = false; };
  }, []);

  // One pass for the strip across the top: how wide the circle is, how much has
  // been fought through together, and the best standing reached with anyone.
  const totals = useMemo(() => {
    if (!rows || !rows.length) return null;
    const missions = rows.reduce((a, r) => a + r.games, 0);
    const clears = rows.reduce((a, r) => a + r.clears, 0);
    const best = rows.reduce((b, r) => (r.score > b.score ? r : b), rows[0]);
    return { people: rows.length, missions, clears, best };
  }, [rows]);

  return (
    <div className="space-y-4">
      {rows === null ? (
        <p className="text-sm text-ink-400 dark:text-ink-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center dark:border-white/10">
          <p className="text-sm font-medium text-ink-800 dark:text-ink-dim">
            {failed ? 'Your relationships could not be loaded.' : 'No squadmates yet.'}
          </p>
          {!failed && (
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Clear an online co-op mission with someone and they will show up here.
            </p>
          )}
        </div>
      ) : (
        <>
          {totals && (
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Squadmates" value={String(totals.people)} />
              <Stat label="Missions together" value={String(totals.missions)} />
              <Stat label="Closest" value={tierOf(totals.best).name} accent />
            </div>
          )}

          {/* The closest squadmate, given the room the question deserves. */}
          <Hero r={rows[0]} />

          {rows.length > 1 && (
            <ol className={'divide-y divide-ink-200 dark:divide-white/10 ' + S_CARD}>
              {rows.slice(1).map((r, i) => <Row key={r.userId} r={r} rank={i + 2} />)}
            </ol>
          )}
        </>
      )}

      {rules && rows !== null && rows.length > 0 && (
        <p className={'text-xs ' + S_MUTED}>
          Clearing a mission together <span className="font-medium text-emerald-600 dark:text-emerald-400">+{rules.clear}</span>
          {' · '}going down together <span className="font-medium text-red-600 dark:text-red-400">{rules.fail}</span>
          {' · '}at most <span className="font-medium text-ink-800 dark:text-ink-dim">+{rules.dailyGainCap}</span> a day with any one person
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={'rounded-xl px-3 py-2.5 text-center ' + S_CARD}>
      <p className={'truncate text-sm font-semibold ' + (accent ? 'text-accent-600 dark:text-accent-400' : 'text-ink-900 dark:text-white')}>{value}</p>
      <p className="mt-0.5 truncate text-[11px] text-ink-500 dark:text-ink-400">{label}</p>
    </div>
  );
}

/** The #1 bond — the one this screen is really about. */
function Hero({ r }: { r: RelationshipRow }) {
  const t = tierOf(r);
  const { pct, toNext, nextName } = progressOf(r);
  const game = GAMES.find((g) => g.id === r.lastGame)?.name ?? r.lastGame;
  const rate = r.games > 0 ? Math.round((r.clears / r.games) * 100) : 0;

  return (
    <div className={'relative overflow-hidden px-4 py-4 ' + S_CARD}>
      <div className="flex items-center gap-3.5">
        <div className="relative shrink-0">
          <Avatar name={r.nickname} src={r.avatar} size={56} />
          {/* The standing, worn on the avatar like a rank badge. */}
          <span className={'absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold text-white shadow ring-2 ring-white dark:ring-ink-900 ' + t.dot}>
            {t.icon}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-ink-900 dark:text-white">
            You &amp; {r.nickname}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={'rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ' + t.chip}>{t.name}</span>
            <span className="text-[11px] tabular-nums text-ink-500 dark:text-ink-400">{r.score} pts</span>
            {r.lastDelta !== 0 && (
              <span className={'text-[11px] tabular-nums font-medium ' + (r.lastDelta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                {r.lastDelta > 0 ? `+${r.lastDelta}` : r.lastDelta} last
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress to the next standing — the "how do I move this" the number alone never answered. */}
      <div className="mt-3.5">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-200/70 dark:bg-white/10">
          <div className={'h-full rounded-full transition-[width] duration-500 ' + t.bar} style={{ width: `${Math.round(pct * 100)}%` }} />
        </div>
        <p className="mt-1.5 text-[11px] text-ink-500 dark:text-ink-400">
          {nextName ? <><span className="font-medium text-ink-700 dark:text-ink-dim">{toNext}</span> to {nextName}</> : 'Top standing — nothing above this.'}
        </p>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-ink-200 pt-3 text-center dark:border-white/10">
        <MiniStat value={String(r.games)} label={r.games === 1 ? 'mission' : 'missions'} />
        <MiniStat value={`${rate}%`} label="survived" />
        <MiniStat value={r.lastAt ? when(r.lastAt) : '—'} label={game ? `last · ${game}` : 'last played'} />
      </div>
    </div>
  );
}

function MiniStat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="truncate text-sm font-semibold text-ink-900 dark:text-white">{value}</p>
      <p className="mt-0.5 truncate text-[11px] text-ink-500 dark:text-ink-400">{label}</p>
    </div>
  );
}

function Row({ r, rank }: { r: RelationshipRow; rank: number }) {
  const t = tierOf(r);
  const { pct, toNext, nextName } = progressOf(r);
  const label = [r.company, r.team != null && r.team > 0 ? `Team ${r.team}` : ''].filter(Boolean).join(' · ');
  const rate = r.games > 0 ? Math.round((r.clears / r.games) * 100) : 0;

  return (
    <li className="flex items-center gap-3.5 px-4 py-3">
      <span className="w-5 shrink-0 text-center text-xs font-semibold tabular-nums text-ink-400 dark:text-ink-500">{rank}</span>
      <div className="relative shrink-0">
        <Avatar name={r.nickname} src={r.avatar} size={40} />
        <span className={'absolute -bottom-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full text-[9px] font-bold text-white ring-2 ring-white dark:ring-ink-900 ' + t.dot}>
          {t.icon}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink-900 dark:text-white">
          {r.nickname}
          {label && <span className="ml-1.5 font-normal text-ink-400 dark:text-ink-500">{label}</span>}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className={'rounded-full px-1.5 py-px text-[10px] font-semibold ring-1 ' + t.chip}>{t.name}</span>
          <span className="truncate text-[11px] text-ink-500 dark:text-ink-400">
            {r.games} {r.games === 1 ? 'mission' : 'missions'} · {rate}% survived{r.lastAt ? ` · ${when(r.lastAt)}` : ''}
          </span>
        </div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-ink-200/70 dark:bg-white/10">
          <div className={'h-full rounded-full ' + t.bar} style={{ width: `${Math.round(pct * 100)}%` }} />
        </div>
      </div>

      <div className="shrink-0 text-right">
        <p className={'text-sm font-semibold leading-tight tabular-nums ' + (r.score < 0 ? 'text-red-500' : 'text-ink-900 dark:text-white')}>
          {r.score}
        </p>
        <p className="text-[10px] text-ink-400 dark:text-ink-500">{nextName ? `${toNext} to go` : 'maxed'}</p>
      </div>
    </li>
  );
}

function Avatar({ name, src, size = 40 }: { name: string; src: string | null; size?: number }) {
  const [broken, setBroken] = useState(false);
  const initials = name.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2).toUpperCase() || '?';
  return (
    <span
      style={{ width: size, height: size }}
      className="grid shrink-0 place-items-center overflow-hidden rounded-full border border-ink-200 bg-ink-100 text-xs font-semibold text-ink-600 dark:border-white/10 dark:bg-ink-950 dark:text-ink-300"
    >
      {src && !broken
        // eslint-disable-next-line @next/next/no-img-element
        ? <img src={src} alt="" className="h-full w-full object-cover" onError={() => setBroken(true)} />
        : initials}
    </span>
  );
}

function when(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const d = Math.floor((Date.now() - t) / 86_400_000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
